'use client';

import {MeinText} from '@components/@typography/MeinText';
import {getChapterIndexByTime} from '@components/LiveEvent/RecordingsPlayer/@utils/getChapterIndexByTime';
import {clamp} from '@utils/clamp';
import {formatTime} from '@utils/formatTime';
import {IChapterCue} from '@utils/parseChapterMarks';
import {motion} from 'framer-motion';
import {FC, RefObject, useMemo} from 'react';
import styles from './ChaptersProgressBarTooltip.module.css';

interface Props {
    cues: IChapterCue[];
    hoveredTime: number | null;
    hoverX: number | null;
    wrapperRef: RefObject<HTMLDivElement | null>;
}

const TOOLTIP_WIDTH = 160;
const SIDE_PADDING = 32;

const ChaptersProgressBarTooltip: FC<Props> = ({
    cues,
    hoveredTime,
    hoverX,
    wrapperRef,
}) => {
    const hoveredCue = useMemo(() => {
        if (hoveredTime === null) {
            return null;
        }
        return cues[getChapterIndexByTime(hoveredTime, cues)];
    }, [hoveredTime, cues]);

    const left = useMemo(() => {
        if (hoverX === null || !wrapperRef.current) {
            return 0;
        }
        const wrapperWidth = wrapperRef.current.getBoundingClientRect().width;
        const clampedX = clamp(
            hoverX,
            SIDE_PADDING + TOOLTIP_WIDTH / 2,
            wrapperWidth - TOOLTIP_WIDTH / 2 - SIDE_PADDING,
        );

        return clampedX;
    }, [hoverX, wrapperRef]);

    if (!hoveredCue || hoveredTime == null) {
        return null;
    }

    return (
        <motion.div
            className={styles.tooltip}
            animate={{
                left,
                y: -15,
                translateX: '-50%',
                opacity: 1,
            }}
            transition={{duration: 0}}
            initial={{opacity: 0, y: 10}}
            exit={{opacity: 0, y: 10}}
        >
            <div className={styles.tooltipContent}>
                <MeinText size="s" className={styles.tooltipTime}>
                    {formatTime(hoveredTime)}
                </MeinText>
                <MeinText size="s" className={styles.tooltipTitle}>
                    {hoveredCue.title}
                </MeinText>
            </div>
        </motion.div>
    );
};

export default ChaptersProgressBarTooltip;
